/**
 * L1 mockup preview — closed product view + flat faces layout (Phase 9.2–9.5).
 *
 * Temporary product UX (no 45° masters yet): front/back presets only.
 * Hide "Drag to rotate" copy and the multi-angle grid; keep dots + swipe to flip sides.
 */
const SHOW_ROTATE_HINT = false;
const SHOW_ALL_ANGLES_WHEN_COUNT = 3;

/**
 * @param {string} baseUrl
 * @param {number|string} cacheBust
 */
export function mockupImageUrl(baseUrl, cacheBust) {
    if (!baseUrl) {
        return '';
    }
    if (cacheBust === 0 || cacheBust === '0' || cacheBust === '' || cacheBust == null) {
        return baseUrl;
    }
    const sep = baseUrl.includes('?') ? '&' : '?';
    return baseUrl + sep + 't=' + encodeURIComponent(String(cacheBust));
}

/**
 * Identity of the painted mockup. Same signature ⇒ do not tear down <img> nodes.
 *
 * @param {{
 *   previewMode: 'closed'|'flat',
 *   activeSide: 'front'|'back',
 *   printSides: 'front_only'|'front_back',
 *   mockupMeta: Record<string, unknown>|null,
 *   designUrls?: { front: string, back: string },
 *   cacheBust?: number|string,
 *   anglesExpanded?: boolean,
 *   activeAngleId?: string|null,
 * }} options
 */
export function mockupViewSignature(options) {
    const angles = parseMockupAngles(options.mockupMeta || null);
    const active = resolveActiveAngle(angles, options.activeAngleId, options.activeSide);
    const designs = options.designUrls || { front: '', back: '' };
    return [
        options.previewMode === 'flat' ? 'flat' : 'closed',
        options.activeSide === 'back' ? 'back' : 'front',
        options.printSides === 'front_back' ? 'front_back' : 'front_only',
        options.mockupMeta ? '1' : '0',
        String(options.cacheBust ?? ''),
        active ? String(active.id) + ':' + String(active.url) : '',
        angles.map((angle) => angle.id + ':' + angle.url).join(','),
        options.anglesExpanded ? '1' : '0',
        String(designs.front || '') + ',' + String(designs.back || ''),
    ].join('|');
}

/**
 * @param {HTMLImageElement} img
 * @param {string} url
 */
function assignImgSrc(img, url) {
    if (!url) {
        if (img.hasAttribute('src')) {
            img.removeAttribute('src');
        }
        return;
    }
    if (img.getAttribute('src') === url) {
        return;
    }
    img.src = url;
}

/**
 * @param {Record<string, unknown>|null} mockupMeta
 * @param {'front'|'back'} side
 * @param {'closed'|'flat'} mode
 */
export function resolveMockupUrl(mockupMeta, side, mode) {
    if (!mockupMeta) {
        return '';
    }
    const key = side + '_' + mode + '_url';
    const url = mockupMeta[key];
    return typeof url === 'string' ? url : '';
}

/**
 * @param {Record<string, unknown>|null} mockupMeta
 * @returns {Array<{id: string, label: string, side: 'front'|'back', url: string}>}
 */
export function parseMockupAngles(mockupMeta) {
    if (!mockupMeta || !Array.isArray(mockupMeta.angles)) {
        return [];
    }

    /** @type {Array<{id: string, label: string, side: 'front'|'back', url: string}>} */
    const angles = [];
    mockupMeta.angles.forEach((entry) => {
        if (!entry || typeof entry !== 'object') {
            return;
        }
        const row = /** @type {Record<string, unknown>} */ (entry);
        const url = row.url;
        if (typeof url !== 'string' || url === '') {
            return;
        }
        const id = typeof row.id === 'string' ? row.id : '';
        const label = typeof row.label === 'string' ? row.label : (id || 'Angle');
        const side = row.side === 'back' ? 'back' : 'front';
        angles.push({ id, label, side, url });
    });

    return angles;
}

/**
 * @param {string} label
 */
function productViewAriaLabel(label) {
    if (SHOW_ROTATE_HINT) {
        return label + ' — Product view. Drag to rotate.';
    }
    return label + ' — Product view. Use dots or swipe for Front and Back.';
}

/**
 * Prefer first preset matching side, else first preset.
 *
 * @param {Array<{id: string, side: 'front'|'back'}>} angles
 * @param {'front'|'back'} side
 * @returns {string|null}
 */
export function resolveAngleIdForSide(angles, side) {
    if (!Array.isArray(angles) || angles.length === 0) {
        return null;
    }
    const match = angles.find((a) => a.side === side);
    return (match || angles[0]).id || null;
}

/**
 * @param {HTMLElement} host
 * @param {{
 *   previewMode: 'closed'|'flat',
 *   activeSide: 'front'|'back',
 *   printSides: 'front_only'|'front_back',
 *   mockupMeta: Record<string, unknown>|null,
 *   designUrls: { front: string, back: string },
 *   template: Record<string, unknown>,
 *   cacheBust: number|string,
 *   anglesExpanded?: boolean,
 *   activeAngleId?: string|null,
 *   onAnglesToggle?: () => void,
 *   onAngleChange?: (angle: {id: string, label: string, side: 'front'|'back', url: string}) => void,
 * }} options
 */
export function renderMockupView(host, options) {
    const {
        previewMode,
        activeSide,
        printSides,
        mockupMeta,
        designUrls,
        template,
        cacheBust,
        anglesExpanded = false,
        activeAngleId = null,
        onAnglesToggle,
        onAngleChange,
    } = options;

    const signature = mockupViewSignature(options);
    if (host.getAttribute('data-tc-mockup-sig') === signature && host.querySelector('.tc-mockup-view')) {
        const angles = parseMockupAngles(mockupMeta);
        const active = resolveActiveAngle(angles, activeAngleId, activeSide);
        if (mockupMeta && previewMode === 'closed' && active && angles.length >= 2 && rotateShellState) {
            rotateShellState.sync(active, onAngleChange);
        }
        return;
    }
    if (rotateShellState && !host.contains(rotateShellState.shell)) {
        rotateShellState = null;
    }
    host.setAttribute('data-tc-mockup-sig', signature);
    host.innerHTML = '';

    const wrap = document.createElement('div');
    wrap.className = 'tc-mockup-view';

    if (mockupMeta) {
        if (previewMode === 'closed') {
            const angles = parseMockupAngles(mockupMeta);
            const active = resolveActiveAngle(angles, activeAngleId, activeSide);

            if (active && angles.length >= 2) {
                wrap.appendChild(acquireRotatableShell(
                    angles,
                    active,
                    cacheBust,
                    onAngleChange,
                ));
            } else if (active) {
                wrap.appendChild(buildMockupFrame(
                    active.url,
                    cacheBust,
                    active.label,
                    'Product view',
                    { hero: true },
                ));
            } else {
                wrap.appendChild(buildMockupFrame(
                    resolveMockupUrl(mockupMeta, activeSide, 'closed'),
                    cacheBust,
                    activeSide === 'back' ? 'Back' : 'Front',
                    'Product view',
                    { hero: true },
                ));
            }

            if (angles.length >= SHOW_ALL_ANGLES_WHEN_COUNT) {
                wrap.appendChild(buildAnglesSection(angles, cacheBust, anglesExpanded, onAnglesToggle));
            }
        } else if (printSides === 'front_back') {
            const grid = document.createElement('div');
            grid.className = 'tc-mockup-view__grid';
            grid.appendChild(buildMockupFrame(
                resolveMockupUrl(mockupMeta, 'front', 'flat'),
                cacheBust,
                'Front',
                'Flat face',
            ));
            grid.appendChild(buildMockupFrame(
                resolveMockupUrl(mockupMeta, 'back', 'flat'),
                cacheBust,
                'Back',
                'Flat face',
            ));
            wrap.appendChild(grid);
        } else {
            wrap.appendChild(buildMockupFrame(
                resolveMockupUrl(mockupMeta, 'front', 'flat'),
                cacheBust,
                'Front',
                'Flat face',
                { hero: true },
            ));
        }
    } else {
        wrap.appendChild(buildFallbackView(template, designUrls, printSides, cacheBust));
    }

    host.appendChild(wrap);
}

/**
 * @param {Array<{id: string, label: string, side: 'front'|'back', url: string}>} angles
 * @param {string|null|undefined} activeAngleId
 * @param {'front'|'back'} activeSide
 */
function resolveActiveAngle(angles, activeAngleId, activeSide) {
    if (!angles.length) {
        return null;
    }
    if (activeAngleId) {
        const byId = angles.find((a) => a.id === activeAngleId);
        if (byId) {
            return byId;
        }
    }
    return angles.find((a) => a.side === activeSide) || angles[0];
}

/**
 * Mockup preset angle (Phase 9.5 contract).
 * @typedef {{id: string, label: string, side: 'front'|'back', url: string}} MockupAngle
 */

/** @type {Set<string>} O2-P1a: every angle URL is prefetched once per session. */
const preloadedAngleUrls = new Set();

/**
 * @param {string[]} urls
 */
function preloadAngleUrls(urls) {
    urls.forEach((url) => {
        if (!url || preloadedAngleUrls.has(url)) {
            return;
        }
        preloadedAngleUrls.add(url);
        const img = new Image();
        img.src = url;
    });
}

/**
 * Persistent rotatable shell (O2-P1a crossfade). Survives angle-only re-renders
 * so a running preset transition is never torn down mid-fade by renderMockupView.
 *
 * @type {{
 *   signature: string,
 *   shell: HTMLElement,
 *   sync: (active: MockupAngle, onAngleChange?: (angle: MockupAngle) => void) => void,
 * }|null}
 */
let rotateShellState = null;

/**
 * Drop the persistent rotate shell (size switch / token change).
 * Next render must build a new <img> for the new mockup URLs.
 */
export function resetRotatableMockupShell() {
    rotateShellState = null;
}

/**
 * Reuse the live rotatable shell across re-renders when the preset set and
 * cache bust are unchanged; rebuild it when the angle assets actually change.
 *
 * @param {MockupAngle[]} angles
 * @param {MockupAngle} active
 * @param {number|string} cacheBust
 * @param {((angle: MockupAngle) => void)|undefined} onAngleChange
 * @returns {HTMLElement}
 */
function acquireRotatableShell(angles, active, cacheBust, onAngleChange) {
    const signature = String(cacheBust) + '|' + angles.map((a) => a.id + ':' + a.url).join('|');
    if (!rotateShellState || rotateShellState.signature !== signature) {
        rotateShellState = createRotatableShell(angles, active, cacheBust, onAngleChange, signature);
    } else {
        rotateShellState.sync(active, onAngleChange);
    }
    return rotateShellState.shell;
}

/**
 * Product view hero with horizontal drag / swipe between presets (Phase 9.5),
 * preset preload + crossfade transition (O2-P1a) and drag-progress mapping
 * with nonlinear commit threshold + flick support (O2-P1b).
 *
 * @param {MockupAngle[]} angles
 * @param {MockupAngle} active
 * @param {number|string} cacheBust
 * @param {((angle: MockupAngle) => void)|undefined} onAngleChange
 * @param {string} signature
 */
function createRotatableShell(angles, active, cacheBust, onAngleChange, signature) {
    const CROSSFADE_MS = 220;

    const shell = document.createElement('div');
    shell.className = 'tc-mockup-view__rotate';

    const frame = document.createElement('figure');
    frame.className = 'tc-mockup-view__frame tc-mockup-view__frame--rotate';
    frame.setAttribute('role', 'img');
    frame.setAttribute('aria-label', productViewAriaLabel(active.label));
    frame.tabIndex = 0;

    const stage = document.createElement('div');
    stage.className = 'tc-mockup-view__stage';

    const imgBase = document.createElement('img');
    imgBase.className = 'tc-mockup-view__img';
    imgBase.alt = active.label + ' — Product view';
    imgBase.loading = 'eager';
    imgBase.decoding = 'async';
    imgBase.draggable = false;
    assignImgSrc(imgBase, mockupImageUrl(active.url, cacheBust));

    const imgOver = document.createElement('img');
    imgOver.className = 'tc-mockup-view__img tc-mockup-view__img--over';
    imgOver.alt = '';
    imgOver.decoding = 'async';
    imgOver.draggable = false;
    imgOver.hidden = true;
    imgOver.setAttribute('aria-hidden', 'true');

    stage.appendChild(imgBase);
    stage.appendChild(imgOver);
    frame.appendChild(stage);

    if (SHOW_ROTATE_HINT) {
        const hint = document.createElement('p');
        hint.className = 'tc-mockup-view__rotate-hint';
        hint.textContent = 'Drag to rotate';
        hint.setAttribute('aria-hidden', 'true');
        frame.appendChild(hint);
    }

    const caption = document.createElement('figcaption');
    caption.className = 'tc-mockup-view__caption small text-muted';
    caption.textContent = active.label;
    frame.appendChild(caption);

    shell.appendChild(frame);

    let current = active;
    /** Angle painted on the overlay (preview / fade layer), if any. */
    let overAngle = null;
    /** Angle being committed via crossfade, if any. */
    let pending = null;
    /** Neighbor angle previewed by the active drag, if any. */
    let dragTarget = null;
    /** Last angle the app state reconciled to (source of truth). */
    let syncedActive = active;
    let commitTimer = 0;

    const dots = document.createElement('div');
    dots.className = 'tc-mockup-view__dots';
    dots.setAttribute('role', 'tablist');
    dots.setAttribute('aria-label', 'Front and back');
    /** @type {HTMLButtonElement[]} */
    const dotButtons = [];
    angles.forEach((angle) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'tc-mockup-view__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', angle.label);
        dot.title = angle.label;
        dot.addEventListener('click', () => {
            if (typeof onAngleChange === 'function' && angle.id !== current.id) {
                onAngleChange(angle);
            }
        });
        dots.appendChild(dot);
        dotButtons.push(dot);
    });
    shell.appendChild(dots);

    const indexOf = (angle) => Math.max(0, angles.findIndex((a) => a.id === angle.id));

    function updateDots(activeId) {
        angles.forEach((angle, i) => {
            const dot = dotButtons[i];
            if (!dot) {
                return;
            }
            const isActive = angle.id === activeId;
            dot.classList.toggle('is-active', isActive);
            dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
    }

    function setOverImage(angle) {
        const url = mockupImageUrl(angle.url, cacheBust);
        assignImgSrc(imgOver, url);
        imgOver.hidden = !url;
        overAngle = angle;
    }

    function clearCommitTimer() {
        if (commitTimer) {
            window.clearTimeout(commitTimer);
            commitTimer = 0;
        }
    }

    /** Paint `angle` on the overlay and fade it in over the committed frame. */
    function startFade(angle) {
        clearCommitTimer();
        setOverImage(angle);
        pending = angle;
        imgOver.classList.add('is-tracking');
        imgOver.style.opacity = '0';
        void imgOver.offsetWidth; // flush so the fade always runs
        imgOver.classList.remove('is-tracking');
        window.requestAnimationFrame(() => {
            imgOver.style.opacity = '1';
        });
        commitTimer = window.setTimeout(commitOverlay, CROSSFADE_MS + 80);
    }

    /** Promote the overlay image to the committed base layer. */
    function commitOverlay() {
        const angle = pending || overAngle;
        if (!angle) {
            return;
        }
        clearCommitTimer();
        pending = null;
        dragTarget = null;
        assignImgSrc(imgBase, mockupImageUrl(angle.url, cacheBust));
        imgBase.alt = angle.label + ' — Product view';
        current = angle;
        imgOver.classList.add('is-tracking');
        imgOver.style.opacity = '0';
        overAngle = null;
        frame.setAttribute('aria-label', productViewAriaLabel(angle.label));
        caption.textContent = angle.label;
        updateDots(angle.id);
        window.requestAnimationFrame(() => {
            imgOver.classList.remove('is-tracking');
        });
    }

    /** Abort any in-flight fade/preview and snap back to the committed frame. */
    function resetOverlay() {
        clearCommitTimer();
        pending = null;
        dragTarget = null;
        if (overAngle) {
            imgOver.classList.add('is-tracking');
            imgOver.style.opacity = '0';
            overAngle = null;
        }
    }

    /** Hide the overlay, then converge to the app state if it moved meanwhile. */
    function dismissOverlay() {
        imgOver.classList.remove('is-tracking');
        imgOver.style.opacity = '0';
        overAngle = null;
        if (syncedActive.id !== current.id) {
            startFade(syncedActive);
        }
    }

    /**
     * @param {number} deltaX
     */
    function neighborOf(deltaX) {
        if (angles.length < 2) {
            return null;
        }
        const next = deltaX < 0
            ? angles[(indexOf(current) + 1) % angles.length]
            : angles[(indexOf(current) - 1 + angles.length) % angles.length];
        return next && next.id !== current.id ? next : null;
    }

    /**
     * Eased drag progress (O2-P1b nonlinear mapping): |deltaX| over ~18% of the
     * frame width, eased in-out so mid-gesture is most responsive.
     *
     * @param {number} deltaX
     */
    function dragProgress(deltaX) {
        const travel = Math.max(160, frame.clientWidth * 0.18);
        const p = Math.min(1, Math.max(0, Math.abs(deltaX) / travel));
        return p < 0.5 ? 2 * p * p : 1 - 2 * (1 - p) * (1 - p);
    }

    /** O2-P1b: neighbor frame opacity follows the eased drag progress 1:1. */
    function dragMove(deltaX) {
        const neighbor = neighborOf(deltaX);
        if (!neighbor) {
            return;
        }
        if (pending && pending.id !== neighbor.id) {
            resetOverlay();
        }
        setOverImage(neighbor);
        dragTarget = neighbor;
        imgOver.classList.add('is-tracking');
        imgOver.style.opacity = String(dragProgress(deltaX));
    }

    /**
     * Commit on release once the eased progress passes the threshold or the
     * gesture is a flick; otherwise animate the preview back (O2-P1b).
     */
    function dragRelease(deltaX, velocity) {
        const neighbor = dragTarget;
        if (!neighbor) {
            return;
        }
        dragTarget = null;
        const progress = dragProgress(deltaX);
        const flick = Math.abs(velocity) > 0.5
            && progress > 0.12
            && Math.sign(velocity) === Math.sign(deltaX);
        if (progress < 0.5 && !flick) {
            dismissOverlay();
            return;
        }
        pending = neighbor;
        imgOver.classList.remove('is-tracking');
        imgOver.style.opacity = '1';
        commitTimer = window.setTimeout(commitOverlay, CROSSFADE_MS + 80);
        if (typeof onAngleChange === 'function') {
            onAngleChange(neighbor);
        }
    }

    function dragCancel() {
        if (dragTarget) {
            dragTarget = null;
            dismissOverlay();
        }
    }

    bindRotateDrag(frame, {
        enabled: typeof onAngleChange === 'function',
        angles,
        currentRef: () => current,
        neighborUrls: () => {
            const visible = mockupImageUrl(current.url, cacheBust);
            return angles
                .map((angle) => mockupImageUrl(angle.url, cacheBust))
                .filter((url) => url && url !== visible);
        },
        dragMove,
        dragRelease,
        dragCancel,
        onKey: (angle) => {
            if (typeof onAngleChange === 'function' && angle.id !== current.id) {
                onAngleChange(angle);
            }
        },
    });

    updateDots(active.id);

    /**
     * Re-render reconciliation: app state stays the source of truth; adopt the
     * latest callback and converge the visual state to `nextActive`.
     */
    function sync(nextActive, nextOnChange) {
        if (typeof nextOnChange === 'function') {
            onAngleChange = nextOnChange;
        }
        syncedActive = nextActive;
        updateDots(nextActive.id);
        if (nextActive.id === current.id && !pending) {
            return;
        }
        if (pending && pending.id === nextActive.id) {
            return; // fade already converging to this preset
        }
        if (dragTarget && dragTarget.id === nextActive.id) {
            return; // release path owns the commit
        }
        startFade(nextActive);
    }

    return { shell, signature, sync };
}

/**
 * Pointer bindings for the rotatable frame (Phase 9.5 + O2 flick velocity).
 *
 * @param {HTMLElement} surface
 * @param {{
 *   enabled: boolean,
 *   angles: MockupAngle[],
 *   currentRef: () => MockupAngle,
 *   neighborUrls?: () => string[],
 *   dragMove: (deltaX: number) => void,
 *   dragRelease: (deltaX: number, velocity: number) => void,
 *   dragCancel: () => void,
 *   onKey: (angle: MockupAngle) => void,
 * }} controller
 */
function bindRotateDrag(surface, controller) {
    if (!controller.enabled || controller.angles.length < 2) {
        return;
    }

    let startX = 0;
    let tracking = false;
    let lastX = 0;
    let lastTime = 0;
    let velocity = 0;

    surface.addEventListener('pointerdown', (event) => {
        if (event.button !== undefined && event.button !== 0) {
            return;
        }
        // Block native image drag / text selection so the pointer sequence completes.
        event.preventDefault();
        if (typeof controller.neighborUrls === 'function') {
            preloadAngleUrls(controller.neighborUrls());
        }
        tracking = true;
        startX = event.clientX;
        lastX = event.clientX;
        lastTime = event.timeStamp;
        velocity = 0;
        surface.classList.add('is-dragging');
        try {
            surface.setPointerCapture(event.pointerId);
        } catch {
            /* ignore */
        }
    });

    surface.addEventListener('pointermove', (event) => {
        if (!tracking) {
            return;
        }
        const deltaX = event.clientX - startX;
        const elapsed = Math.max(1, event.timeStamp - lastTime);
        velocity = velocity * 0.8 + ((event.clientX - lastX) / elapsed) * 0.2;
        lastX = event.clientX;
        lastTime = event.timeStamp;
        if (Math.abs(deltaX) > 8) {
            event.preventDefault();
        }
        controller.dragMove(deltaX);
    });

    surface.addEventListener('pointerup', (event) => {
        if (!tracking) {
            return;
        }
        tracking = false;
        surface.classList.remove('is-dragging');
        controller.dragRelease(event.clientX - startX, velocity);
        velocity = 0;
    });

    surface.addEventListener('pointercancel', () => {
        tracking = false;
        velocity = 0;
        surface.classList.remove('is-dragging');
        controller.dragCancel();
    });

    surface.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
            return;
        }
        event.preventDefault();
        const current = controller.currentRef();
        const index = Math.max(0, controller.angles.findIndex((a) => a.id === current.id));
        const nextIndex = event.key === 'ArrowRight'
            ? (index + 1) % controller.angles.length
            : (index - 1 + controller.angles.length) % controller.angles.length;
        const next = controller.angles[nextIndex];
        if (next && next.id !== current.id) {
            controller.onKey(next);
        }
    });
}

/**
 * @param {string} src
 * @param {number|string} cacheBust
 * @param {string} sideLabel
 * @param {string} modeLabel
 * @param {{hero?: boolean}} [options] hero: single-frame stage fill (not grid cells)
 */
function buildMockupFrame(src, cacheBust, sideLabel, modeLabel, options = {}) {
    const hero = options.hero === true;
    const frame = document.createElement('figure');
    frame.className = hero
        ? 'tc-mockup-view__frame tc-mockup-view__frame--hero'
        : 'tc-mockup-view__frame';

    const img = document.createElement('img');
    img.className = 'tc-mockup-view__img';
    img.alt = sideLabel + ' — ' + modeLabel;
    img.loading = 'eager';
    img.decoding = 'async';
    assignImgSrc(img, mockupImageUrl(src, cacheBust));

    if (hero) {
        const stage = document.createElement('div');
        stage.className = 'tc-mockup-view__stage';
        stage.appendChild(img);
        frame.appendChild(stage);
    } else {
        frame.appendChild(img);
    }

    const caption = document.createElement('figcaption');
    caption.className = 'tc-mockup-view__caption small text-muted';
    caption.textContent = sideLabel;

    frame.appendChild(caption);

    return frame;
}

/**
 * Collapsible preset-angle grid (Phase 9.3).
 *
 * @param {Array<{id: string, label: string, url: string}>} angles
 * @param {number|string} cacheBust
 * @param {boolean} expanded
 * @param {(() => void)|undefined} onToggle
 */
function buildAnglesSection(angles, cacheBust, expanded, onToggle) {
    const section = document.createElement('div');
    section.className = 'tc-mockup-view__angles';

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'btn btn-link tc-inline-link tc-mockup-view__angles-toggle p-0 mt-3';
    toggle.textContent = expanded ? 'Hide angles' : 'View all angles';
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    toggle.addEventListener('click', () => {
        if (typeof onToggle === 'function') {
            onToggle();
        }
    });
    section.appendChild(toggle);

    if (expanded) {
        const grid = document.createElement('div');
        grid.className = 'tc-mockup-view__angles-grid';
        angles.forEach((angle) => {
            grid.appendChild(buildMockupFrame(
                angle.url,
                cacheBust,
                angle.label,
                'Product angle',
            ));
        });
        section.appendChild(grid);
    }

    return section;
}

/**
 * Non-mockup SKU fallback: flat compositor artwork or list thumbnail (Phase 9.2).
 *
 * @param {Record<string, unknown>} template
 * @param {{ front: string, back: string }} designUrls
 * @param {'front_only'|'front_back'} printSides
 * @param {number|string} cacheBust
 */
function buildFallbackView(template, designUrls, printSides, cacheBust) {
    const grid = document.createElement('div');
    grid.className = 'tc-mockup-view__grid';

    const sides = printSides === 'front_back' ? ['front', 'back'] : ['front'];
    sides.forEach((side) => {
        const label = side === 'back' ? 'Back' : 'Front';
        const designUrl = side === 'back' ? designUrls.back : designUrls.front;
        const listImage = typeof template.preview_image === 'string' ? template.preview_image : '';
        const src = designUrl || listImage;

        const frame = document.createElement('figure');
        frame.className = 'tc-mockup-view__frame tc-mockup-view__frame--fallback';

        if (src) {
            const img = document.createElement('img');
            img.className = 'tc-mockup-view__img';
            img.alt = label + ' preview';
            img.loading = 'eager';
            img.decoding = 'async';
            assignImgSrc(img, designUrl
                ? mockupImageUrl(designUrl, cacheBust)
                : listImage);
            frame.appendChild(img);
        } else {
            const empty = document.createElement('div');
            empty.className = 'tc-mockup-view__empty';
            empty.textContent = 'Generate a design to preview your tote.';
            frame.appendChild(empty);
        }

        const caption = document.createElement('figcaption');
        caption.className = 'tc-mockup-view__caption small text-muted';
        caption.textContent = label;
        frame.appendChild(caption);

        grid.appendChild(frame);
    });

    const note = document.createElement('p');
    note.className = 'tc-mockup-view__fallback-note small text-muted mb-0 mt-2';
    note.textContent = 'Full product mockup coming soon for this template — showing flat print preview.';

    const wrap = document.createElement('div');
    wrap.className = 'tc-mockup-view__fallback';
    wrap.appendChild(grid);
    wrap.appendChild(note);

    return wrap;
}
